import { Content, Editor, JSONContent } from "@tiptap/core";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import React, { useEffect, useRef } from "react";
import omit from "lodash/omit";
import {
  For,
  LabelProp,
  getCorrectHtmlAttributesForInput,
  normalizeLabelProp,
} from "../input-general/label-prop";
import ValidationMessage, {
  invalidInputOutlineClasses,
} from "../input-general/validation";
import { focusHasLeftElement } from "../utils/focus-has-left-element";
import { useHtmlId } from "@/flokit/utils/html/use-html-id";
import InputLabel from "../input-general/input-label";

import { cx } from "class-variance-authority";
import { CharacterLimit } from "@/flokit/compound/inputs/text-area-input/character-limit";

const RichTextContext = React.createContext<{
  editor: Editor | null;
}>({
  editor: null,
});
export const useRichTextContext = () => React.useContext(RichTextContext);

export const extensions = [
  StarterKit.configure({}),
  Underline,
  Link.configure({
    validate: (href) => /^https?:\/\//.test(href),
  }),
];

export const RichTextEditorBase = (props: {
  content: Content;
  onChange?: (html: string, plainText: string, json: JSONContent) => void;
  children?: React.ReactNode;
  readOnly?: boolean;
  editorClassName?: string;
  required?: boolean;
  tooltip?: string;
  textClassName?: string;
  placeholder?: string;
  shouldShowValidation?: boolean;
  validationMessage?: string;
  label?: Exclude<LabelProp, For>;
  showBorder?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  characterLimit?: number;
  minimumCharacterLimitToDisplay?: number;
}) => {
  const id = useHtmlId();
  const characterLimitId = useHtmlId();
  const labelMethod = props.label ? normalizeLabelProp(props.label) : null;
  const editorLabelAttributes = props.label
    ? {
        role: "textbox",
        ...omit(
          getCorrectHtmlAttributesForInput({
            labelProp: props.label,
            htmlId: id,
            elementIsNonInteractive: true,
            fieldIsRequired: false,
          }),
          "required",
        ),
      }
    : null;

  const onChange = props.onChange || (() => {});
  const updateCallback = useRef(onChange);
  updateCallback.current = onChange;
  const editor = useEditor({
    extensions: [
      ...extensions,
      Placeholder.configure({
        placeholder: props.placeholder || "",
      }),
    ],
    content: props.content,
    onUpdate({ editor }) {
      updateCallback.current(
        editor.getHTML(),
        editor.getText(),
        editor.getJSON(),
      );
    },
    editorProps: {
      attributes: {
        class: props.editorClassName,
        ...editorLabelAttributes,
      },
    },
    editable: !props.readOnly,
  });

  useEffect(() => {
    if (editor) {
      const current = editor.getHTML();
      if (current !== props.content) {
        editor.commands.setContent(props.content);
      }
    }
  }, [props.content, editor]);

  useEffect(() => {
    if (editor) {
      editor.setOptions({ editable: !props.readOnly });
    }
  }, [props.readOnly, editor]);

  return (
    <RichTextContext.Provider
      value={{
        editor,
      }}
    >
      <div className="space-y-2">
        <div className="flex flex-row items-center justify-between">
          <div>
            {props.label ? (
              <InputLabel
                id={id}
                labelMethod={labelMethod}
                required={props.required}
                tooltip={props.tooltip}
                isElementInteractive={false}
              />
            ) : null}
          </div>
          {props.characterLimit ? (
            <CharacterLimit
              currentLength={editor?.getHTML().length ?? 0}
              characterLimit={props.characterLimit}
              minimumCharacterLimitToDisplay={
                props.minimumCharacterLimitToDisplay
              }
              shouldShowValidation={props.shouldShowValidation ?? false}
              id={characterLimitId}
            />
          ) : null}
        </div>
        <div
          className={cx(
            "w-full rounded-md",
            props.shouldShowValidation
              ? invalidInputOutlineClasses
              : !props.showBorder
                ? ""
                : "border border-dublin-gray",
          )}
          onBlur={(e) => {
            if (focusHasLeftElement(e) && props.onBlur) {
              props.onBlur(e);
            }
          }}
        >
          {props.children}
          <EditorContent editor={editor} className={props.textClassName} />
          <ValidationMessage
            shouldShowValidation={props.shouldShowValidation}
            validationMessage={props.validationMessage}
          />
        </div>
      </div>
    </RichTextContext.Provider>
  );
};
